import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ExternalLink, Mail, Mountain, Pencil, Plus, Shield, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { EmptyState } from '../components/ui/empty-state'
import { MetricCard } from '../components/ui/metric-card'
import { MeasurementInput } from '../components/ui/measurement'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import { useMeasurements } from '../hooks/useMeasurements'
import { createTour, deleteTour, getTourOperator, TourOperator, TourSummary, updateTour } from '../lib/api/tourOperators'
import { unitLabelFor, type MeasurementUnit } from '../lib/measurements'
import { TOUR_PLAN_GENERATION_PROFILES, planGenerationProfileLabel } from '../lib/planGenerationProfile'
import {
  addDay,
  createEmptyTourForm,
  DayFormState,
  mapTourToForm,
  MAX_ITINERARY_DAYS,
  removeDay,
  TourFormState,
  tourFormToPayload,
  updateDayField
} from '../utils/tourForm'

const sumDistanceKm = (tour: TourSummary) => {
  return tour.itinerary.reduce((total, day) => total + (day.distanceKm ?? 0), 0)
}

const parseMetricFormValue = (value?: string | null) => {
  if (!value?.trim()) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const formMetricString = (value: number | null) => (value == null ? '' : value.toString())

export const TourAdminWorkspacePage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const measurements = useMeasurements()
  const operatorId = user?.operatorId ?? null

  const [operator, setOperator] = useState<TourOperator | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingTourId, setEditingTourId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<TourFormState | null>(null)
  const [newTourForm, setNewTourForm] = useState<TourFormState>(() => createEmptyTourForm(measurements.unit))
  const [savingTourId, setSavingTourId] = useState<string | null>(null)
  const [creatingTour, setCreatingTour] = useState(false)
  const [deletingTourId, setDeletingTourId] = useState<string | null>(null)

  const loadOperator = useCallback(async () => {
    if (!operatorId) {
      setOperator(null)
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const data = await getTourOperator(operatorId)
      setOperator(data)
    } catch (err) {
      console.error('Failed to load tour operator', err)
      setError('Unable to load your operator details right now.')
    } finally {
      setLoading(false)
    }
  }, [operatorId])

  useEffect(() => {
    void loadOperator()
  }, [loadOperator])

  const metrics = useMemo(() => {
    const tours = operator?.tours ?? []
    const totalDays = tours.reduce((sum, tour) => sum + tour.itinerary.length, 0)
    const totalKm = tours.reduce((sum, tour) => sum + sumDistanceKm(tour), 0)
    const linkedTours = tours.filter((tour) => Boolean(tour.link)).length
    const longestTour = tours.reduce<{ name: string; days: number }>(
      (current, tour) => {
        if (tour.itinerary.length > current.days) {
          return { name: tour.name, days: tour.itinerary.length }
        }
        return current
      },
      { name: '', days: 0 }
    )

    return {
      totalTours: tours.length,
      totalDays,
      totalKm,
      linkedTours,
      longestLabel: longestTour.days > 0 ? `${longestTour.name} (${longestTour.days} days)` : '—'
    }
  }, [operator?.tours])

  const handleStartEditing = (tour: TourSummary) => {
    setEditingTourId(tour.id)
    setEditForm(mapTourToForm(tour, measurements.unit))
  }

  const handleCancelEditing = () => {
    setEditingTourId(null)
    setEditForm(null)
  }

  const validateForm = (form: TourFormState | null) => {
    if (!form) {
      addToast({ variant: 'error', title: 'Nothing to save', description: 'Select a tour before editing.' })
      return false
    }
    if (!form.name.trim()) {
      addToast({ variant: 'error', title: 'Tour name required', description: 'Add a name before saving your changes.' })
      return false
    }
    if (form.itinerary.length === 0) {
      addToast({ variant: 'error', title: 'Itinerary missing', description: 'Add at least one day to the itinerary.' })
      return false
    }
    return true
  }

  const handleSaveTour = async () => {
    if (!operator || !editingTourId) {
      return
    }

    if (!validateForm(editForm)) {
      return
    }

    const payload = tourFormToPayload(editForm!)

    setSavingTourId(editingTourId)
    try {
      const updated = await updateTour(editingTourId, payload)
      setOperator((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          tours: prev.tours.map((tour) => (tour.id === updated.id ? updated : tour))
        }
      })
      setEditForm(mapTourToForm(updated, measurements.unit))
      addToast({ variant: 'success', title: 'Tour updated', description: `${updated.name} saved successfully.` })
    } catch (err) {
      console.error('Failed to update tour', err)
      addToast({ variant: 'error', title: 'Unable to save tour', description: 'Check your connection and try again.' })
    } finally {
      setSavingTourId(null)
    }
  }

  const handleCreateTour = async () => {
    if (!operator) {
      return
    }

    if (!validateForm(newTourForm)) {
      return
    }

    const payload = tourFormToPayload(newTourForm)
    setCreatingTour(true)
    try {
      const created = await createTour(operator.id, payload)
      setOperator((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          tours: [...prev.tours, created]
        }
      })
      setNewTourForm(createEmptyTourForm(measurements.unit))
      addToast({ variant: 'success', title: 'Tour created', description: `${created.name} is now live in your workspace.` })
    } catch (err) {
      console.error('Failed to create tour', err)
      addToast({ variant: 'error', title: 'Unable to create tour', description: 'Please try again or contact a full admin.' })
    } finally {
      setCreatingTour(false)
    }
  }

  const handleDeleteTour = async (tourId: string) => {
    if (!window.confirm('Delete this tour? This cannot be undone.')) {
      return
    }
    setDeletingTourId(tourId)
    try {
      await deleteTour(tourId)
      setOperator((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          tours: prev.tours.filter((tour) => tour.id !== tourId)
        }
      })
      if (editingTourId === tourId) {
        setEditingTourId(null)
        setEditForm(null)
      }
      addToast({ variant: 'success', title: 'Tour deleted', description: 'The tour has been removed from your workspace.' })
    } catch (err) {
      console.error('Failed to delete tour', err)
      addToast({ variant: 'error', title: 'Unable to delete tour', description: 'Try again or contact a full admin.' })
    } finally {
      setDeletingTourId(null)
    }
  }

  const handleEditItineraryChange = (index: number, field: keyof DayFormState, value: string) => {
    setEditForm((prev) => (prev ? updateDayField(prev, index, field, value) : prev))
  }

  const handleNewItineraryChange = (index: number, field: keyof DayFormState, value: string) => {
    setNewTourForm((prev) => updateDayField(prev, index, field, value))
  }

  const addDayToEditForm = () => {
    setEditForm((prev) => (prev ? addDay(prev) : prev))
  }

  const addDayToNewForm = () => {
    setNewTourForm((prev) => addDay(prev))
  }

  const removeDayFromEditForm = (index: number) => {
    setEditForm((prev) => (prev ? removeDay(prev, index) : prev))
  }

  const removeDayFromNewForm = (index: number) => {
    setNewTourForm((prev) => removeDay(prev, index))
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="h-28 animate-pulse rounded-2xl border border-border/50 bg-muted/40" />
        ))}
      </div>
    )
  }

  if (!operatorId) {
    return (
      <EmptyState
        icon={Shield}
        title="No operator linked"
        description="TourAdmin accounts must be linked to a tour operator. Contact a full admin so they can assign your company before continuing."
        className="mt-10 rounded-2xl border border-dashed border-rose-200 bg-rose-50/50"
      >
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button asChild variant="accent">
            <Link to="/ask">Message HQ</Link>
          </Button>
          <Button asChild variant="outline">
            <a href="mailto:operations@hikestrong.com">Email admins</a>
          </Button>
        </div>
      </EmptyState>
    )
  }

  if (error) {
    return (
      <Card className="border border-rose-200 bg-rose-50/70">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="h-5 w-5" />
            Unable to load workspace
          </CardTitle>
          <CardDescription className="text-rose-600">{error}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => loadOperator()} variant="accent">
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (!operator) {
    return null
  }

  const tours = operator.tours

  return (
    <div className="space-y-5">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">TourAdmin workspace</p>
          <h1 className="text-3xl font-semibold text-foreground">Operator: {operator.name}</h1>
          <p className="text-sm text-muted-foreground">
            Everything you need to manage the tours for <span className="font-semibold text-foreground">{operator.name}</span>.
          </p>
        </div>
        <div className="rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-primary">
          <p className="font-semibold uppercase tracking-widest text-[10px] text-primary">Scoped access</p>
          <p>You can only edit tours assigned to {operator.name}. Contact HQ for wider access.</p>
        </div>
      </header>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          title="Tours in portfolio"
          value={metrics.totalTours}
          description="Active itineraries mapped to your operator."
          icon={Mountain}
        />
        <MetricCard
          title="Itinerary days"
          value={metrics.totalDays}
          description="Total days across every published tour."
          icon={Shield}
          variant="accent"
        />
        <MetricCard
          title="Trail distance"
          value={measurements.format(metrics.totalKm, 'distance')}
          description="Aggregate distance calculated from each itinerary day."
        />
        <MetricCard
          title="Tours with links"
          value={metrics.totalTours > 0 ? `${metrics.linkedTours}/${metrics.totalTours}` : '0/0'}
          description={metrics.longestLabel === '—' ? 'Publish at least one tour link.' : `Longest route: ${metrics.longestLabel}`}
          icon={ExternalLink}
          variant="success"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Tours for {operator.name}</CardTitle>
            <CardDescription>Inline editing keeps itineraries scoped to your operator.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {tours.length === 0 && (
              <EmptyState
                icon={Mountain}
                title="No tours available"
                description="Contact a full admin to import tours for your operator."
                className="border border-dashed border-border/60 bg-muted/20"
              />
            )}

            {tours.map((tour) => {
              const tourDistance = sumDistanceKm(tour)
              const isEditing = editingTourId === tour.id
              const savingThisTour = savingTourId === tour.id
              const form = isEditing ? editForm : null

              return (
                <div key={tour.id} className="rounded-2xl border border-border/70 bg-card/70 p-4 shadow-sm">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">{tour.name}</h3>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">
                        {tour.itinerary.length} day itinerary • {measurements.format(tourDistance, 'distance')} tracked
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => handleStartEditing(tour)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit tour
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!tour.link}
                        asChild={Boolean(tour.link)}
                      >
                        {tour.link ? (
                          <a href={tour.link} target="_blank" rel="noreferrer">
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Open link
                          </a>
                        ) : (
                          <>
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Link missing
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {isEditing && form && (
                    <div className="mt-4 space-y-4 rounded-xl border border-border/60 bg-muted/20 p-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label htmlFor="workspace-tour-name" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tour name</label>
                          <Input
                            id="workspace-tour-name"
                            value={form.name}
                            onChange={(event) => setEditForm((prev) => (prev ? { ...prev, name: event.target.value } : prev))}
                            placeholder="Summit link-up itinerary"
                          />
                        </div>
                        <div>
                          <label htmlFor="workspace-tour-link" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Public link</label>
                          <Input
                            id="workspace-tour-link"
                            value={form.link}
                            onChange={(event) => setEditForm((prev) => (prev ? { ...prev, link: event.target.value } : prev))}
                            placeholder="https://example.com/tour"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor={`workspace-tour-description-${tour.id}`} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tour description</label>
                        <Textarea
                          id={`workspace-tour-description-${tour.id}`}
                          value={form.description}
                          onChange={(event) => setEditForm((prev) => (prev ? { ...prev, description: event.target.value } : prev))}
                          placeholder="Describe what makes this itinerary unique."
                          maxLength={4000}
                        />
                        <p className="mt-1 text-xs text-muted-foreground">Shown after the generated distance and elevation summary. Leave blank to use the generic tour copy.</p>
                      </div>

                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Itinerary details</p>
                            <p className="text-xs text-muted-foreground">Values use {form.sourceMeasurementUnit === 'Imperial' ? 'Imperial (mi, ft, lb)' : 'Metric (km, m, kg)'} units.</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={addDayToEditForm}
                              disabled={form.itinerary.length >= MAX_ITINERARY_DAYS}
                            >
                              <Plus className="mr-1 h-4 w-4" />
                              Add day
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleStartEditing(tour)}>
                              Reset fields
                            </Button>
                          </div>
                        </div>

                        <label className="block space-y-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Measurement system</span>
                          <select
                            aria-label={`Measurement system for ${tour.name}`}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            value={form.sourceMeasurementUnit}
                            onChange={(event) => setEditForm((prev) => (prev ? { ...prev, sourceMeasurementUnit: event.target.value as MeasurementUnit } : prev))}
                          >
                            <option value="Metric">Metric (km, m, kg)</option>
                            <option value="Imperial">Imperial (mi, ft, lb)</option>
                          </select>
                        </label>

                        <label className="block space-y-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Training plan profile</span>
                          <select
                            aria-label={`Training plan profile for ${tour.name}`}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            value={form.planGenerationProfile}
                            onChange={(event) => setEditForm((prev) => (prev ? { ...prev, planGenerationProfile: event.target.value as TourFormState['planGenerationProfile'] } : prev))}
                          >
                            {TOUR_PLAN_GENERATION_PROFILES.map((profile) => (
                              <option key={profile} value={profile}>{planGenerationProfileLabel(profile)}</option>
                            ))}
                          </select>
                          <span className="text-xs text-muted-foreground">Day hike removes the standard weekly distance and elevation floors.</span>
                        </label>

                        <div className="space-y-3">
                          {form.itinerary.map((day, index) => (
                            <div
                              key={`${tour.id}-day-${index}`}
                              className="grid gap-3 md:grid-cols-[repeat(4,minmax(0,1fr))_auto]"
                            >
                              <div>
                                <label htmlFor={`workspace-day-${index}`} className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Day #</label>
                                <Input
                                  id={`workspace-day-${index}`}
                                  type="number"
                                  min={1}
                                  max={MAX_ITINERARY_DAYS}
                                  value={day.dayNumber}
                                  onChange={(event) => handleEditItineraryChange(index, 'dayNumber', event.target.value)}
                                />
                              </div>
                              <div>
                                <label htmlFor={`workspace-km-${index}`} className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Distance ({unitLabelFor('distance', form.sourceMeasurementUnit)})</label>
                                <MeasurementInput
                                  id={`workspace-km-${index}`}
                                  kind="distance"
                                  unit={form.sourceMeasurementUnit}
                                  valueMetric={parseMetricFormValue(day.distanceKm)}
                                  onValueMetricChange={(value) => handleEditItineraryChange(index, 'distanceKm', formMetricString(value))}
                                />
                              </div>
                              <div>
                                <label htmlFor={`workspace-gain-${index}`} className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Gain ({unitLabelFor('elevation', form.sourceMeasurementUnit)})</label>
                                <MeasurementInput
                                  id={`workspace-gain-${index}`}
                                  kind="elevation"
                                  unit={form.sourceMeasurementUnit}
                                  valueMetric={parseMetricFormValue(day.elevationGainMeters)}
                                  onValueMetricChange={(value) => handleEditItineraryChange(index, 'elevationGainMeters', formMetricString(value))}
                                />
                              </div>
                              <div>
                                <label htmlFor={`workspace-loss-${index}`} className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Loss ({unitLabelFor('elevation', form.sourceMeasurementUnit)})</label>
                                <MeasurementInput
                                  id={`workspace-loss-${index}`}
                                  kind="elevation"
                                  unit={form.sourceMeasurementUnit}
                                  valueMetric={parseMetricFormValue(day.elevationLossMeters)}
                                  onValueMetricChange={(value) => handleEditItineraryChange(index, 'elevationLossMeters', formMetricString(value))}
                                />
                              </div>
                              <div className="flex items-end">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  type="button"
                                  onClick={() => removeDayFromEditForm(index)}
                                  disabled={form.itinerary.length === 1}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <Button size="sm" onClick={() => handleSaveTour()} disabled={!form.name.trim() || savingThisTour}>
                          {savingThisTour ? 'Saving…' : 'Save changes'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={handleCancelEditing} disabled={savingThisTour || deletingTourId === tour.id}>
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-rose-200 text-rose-600 hover:bg-rose-50"
                          onClick={() => handleDeleteTour(tour.id)}
                          disabled={deletingTourId === tour.id}
                        >
                          {deletingTourId === tour.id ? 'Deleting…' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Create new tour</CardTitle>
              <CardDescription>Spin up a fresh itinerary scoped to {operator.name}.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="Tour name"
                value={newTourForm.name}
                onChange={(event) => setNewTourForm((prev) => ({ ...prev, name: event.target.value }))}
              />
              <Input
                placeholder="Public link (optional)"
                value={newTourForm.link}
                onChange={(event) => setNewTourForm((prev) => ({ ...prev, link: event.target.value }))}
              />
              <Textarea
                aria-label="Tour description"
                placeholder="Tour description (optional)"
                value={newTourForm.description}
                onChange={(event) => setNewTourForm((prev) => ({ ...prev, description: event.target.value }))}
                maxLength={4000}
              />
              <p className="text-xs text-muted-foreground">Shown after the generated distance and elevation summary. Leave blank to use the generic tour copy.</p>

              <label className="block space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Measurement system</span>
                <select
                  aria-label="New tour measurement system"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  value={newTourForm.sourceMeasurementUnit}
                  onChange={(event) => setNewTourForm((prev) => ({ ...prev, sourceMeasurementUnit: event.target.value as MeasurementUnit }))}
                >
                  <option value="Metric">Metric (km, m, kg)</option>
                  <option value="Imperial">Imperial (mi, ft, lb)</option>
                </select>
              </label>

              <label className="block space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Training plan profile</span>
                <select
                  aria-label="New tour training plan profile"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  value={newTourForm.planGenerationProfile}
                  onChange={(event) => setNewTourForm((prev) => ({ ...prev, planGenerationProfile: event.target.value as TourFormState['planGenerationProfile'] }))}
                >
                  {TOUR_PLAN_GENERATION_PROFILES.map((profile) => (
                    <option key={profile} value={profile}>{planGenerationProfileLabel(profile)}</option>
                  ))}
                </select>
                <span className="text-xs text-muted-foreground">Day hike removes the standard weekly distance and elevation floors.</span>
              </label>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Itinerary</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={addDayToNewForm}
                    disabled={newTourForm.itinerary.length >= MAX_ITINERARY_DAYS}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add day
                  </Button>
                </div>
                <div className="space-y-3">
                  {newTourForm.itinerary.map((day, index) => (
                    <div
                      key={`new-tour-day-${index}`}
                      className="grid gap-3 md:grid-cols-[repeat(4,minmax(0,1fr))_auto]"
                    >
                      <Input
                        type="number"
                        min={1}
                        max={MAX_ITINERARY_DAYS}
                        value={day.dayNumber}
                        onChange={(event) => handleNewItineraryChange(index, 'dayNumber', event.target.value)}
                      />
                      <MeasurementInput
                        kind="distance"
                        unit={newTourForm.sourceMeasurementUnit}
                        placeholder={`Distance (${unitLabelFor('distance', newTourForm.sourceMeasurementUnit)})`}
                        valueMetric={parseMetricFormValue(day.distanceKm)}
                        onValueMetricChange={(value) => handleNewItineraryChange(index, 'distanceKm', formMetricString(value))}
                      />
                      <MeasurementInput
                        kind="elevation"
                        unit={newTourForm.sourceMeasurementUnit}
                        placeholder={`Gain (${unitLabelFor('elevation', newTourForm.sourceMeasurementUnit)})`}
                        valueMetric={parseMetricFormValue(day.elevationGainMeters)}
                        onValueMetricChange={(value) => handleNewItineraryChange(index, 'elevationGainMeters', formMetricString(value))}
                      />
                      <MeasurementInput
                        kind="elevation"
                        unit={newTourForm.sourceMeasurementUnit}
                        placeholder={`Loss (${unitLabelFor('elevation', newTourForm.sourceMeasurementUnit)})`}
                        valueMetric={parseMetricFormValue(day.elevationLossMeters)}
                        onValueMetricChange={(value) => handleNewItineraryChange(index, 'elevationLossMeters', formMetricString(value))}
                      />
                      <div className="flex items-center justify-end">
                        <Button
                          size="icon"
                          variant="ghost"
                          type="button"
                          onClick={() => removeDayFromNewForm(index)}
                          disabled={newTourForm.itinerary.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button onClick={() => handleCreateTour()} disabled={creatingTour || !newTourForm.name.trim()}>
                  {creatingTour ? 'Creating…' : 'Create tour'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setNewTourForm(createEmptyTourForm(measurements.unit))}
                  disabled={creatingTour}
                >
                  Reset form
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick access</CardTitle>
              <CardDescription>Need to adjust multiple tours? Jump into these shortcuts.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full" onClick={() => void loadOperator()}>
                Refresh operator data
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/ask">Ask HQ for help</Link>
              </Button>
              <Button asChild variant="ghost" className="w-full text-sm text-muted-foreground">
                <a href="mailto:operations@hikestrong.com">
                  <Mail className="mr-2 h-4 w-4" />
                  Email full admin team
                </a>
              </Button>
            </CardContent>
          </Card>

          <Card className="border border-amber-200 bg-amber-50/70">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-900">
                <AlertTriangle className="h-5 w-5" />
                Need full admin backup?
              </CardTitle>
              <CardDescription className="text-amber-900/80">
                Some actions (new operators, billing changes, cross-operator edits) still require a full admin.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-amber-900/90">
              <p>Share the operator ID below when contacting HQ so they can act quickly.</p>
              <div className="rounded-xl border border-amber-200 bg-white/70 px-4 py-3 font-mono text-xs uppercase tracking-wider text-amber-900">
                {operator.id}
              </div>
              <div className="flex flex-wrap gap-3">
                <Button asChild variant="accent" size="sm">
                  <Link to="/ask">Start admin request</Link>
                </Button>
                <Button asChild variant="outline" size="sm">
                  <a href="mailto:operations@hikestrong.com?subject=TourAdmin%20help">Email HQ</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
